import { useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material'
import { GetApp as DownloadIcon } from '@mui/icons-material'
import {
  CancelIcon,
  ConfirmIcon,
  DeleteIcon,
  RecertificationIcon,
} from 'src/v3/core/icons'
import axios from 'src/utilities/http-client'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { fetchCertificatesRollup } from 'src/v3/profile/profile.slice'
import { selectUseRecertificationCycle } from 'src/v3/settings/settings.slice'
import { CertificateRollupItem } from '../types'
import { useCertificateDownload } from '../hooks/use-certificate-download'
import { ExternalCertificateForm } from './external-certificate-form'

interface CertificateDownloadCellProps {
  item: CertificateRollupItem
}

export default function CertificateDownloadCell({
  item,
}: CertificateDownloadCellProps) {
  let dispatch = useAppDispatch()
  let useRecertificationCycle = useAppSelector(selectUseRecertificationCycle)
  let { downloadCertificate, isDownloading } = useCertificateDownload()
  let [allocateID, setAllocateID] = useState('')
  let [deleteID, setDeleteID] = useState('')
  let [deleting, setDeleting] = useState(false)

  let handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation()
    let result = await downloadCertificate(item)
    if (!result.success && result.error) {
      // Could show a toast notification here in the future
    }
  }

  if (!item.certificateID || item.type === 'attendance') {
    return <span style={{ color: 'gray' }}>No Certificate</span>
  }

  let isCurrentlyDownloading = isDownloading(item.certificateID)

  return (
    <Box>
      <Button
        size="small"
        variant="text"
        startIcon={<DownloadIcon />}
        onClick={handleDownload}
        disabled={isCurrentlyDownloading}
      >
        {isCurrentlyDownloading ? 'Downloading...' : 'Download'}
      </Button>
      {item.type === 'external' && (
        <>
          {useRecertificationCycle && (
            <Button
              color="secondary"
              sx={{
                mx: 0.5,
              }}
              size="small"
              variant="text"
              startIcon={<RecertificationIcon />}
              onClick={() => setAllocateID(item.certificateID || '')}
              disabled={deleting}
            >
              Allocate
            </Button>
          )}
          <Button
            color="error"
            size="small"
            variant="text"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteID(item.certificateID || '')}
            disabled={deleting}
          >
            Delete
          </Button>
        </>
      )}
      <ExternalCertificateForm
        certificateID={allocateID}
        open={!!allocateID}
        setOpen={() => setAllocateID('')}
      />
      <Dialog open={!!deleteID} onClose={() => setDeleteID('')}>
        <DialogTitle id="confirm-dialog-title" sx={{ textAlign: 'center' }}>
          Confirm Deletion
        </DialogTitle>
        <DialogContent id="confirm-delete-certificate">
          Are you sure you want to delete this external certificate?
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, pt: 0 }}>
          <Button
            color="primary"
            onClick={() => {
              setDeleting(true)
              axios
                .delete(`/api/v2/profile/external-certificate?id=${deleteID}`)
                .then(() => dispatch(fetchCertificatesRollup()))
                .finally(() => {
                  setDeleteID('')
                  setDeleting(false)
                })
            }}
            variant="contained"
            sx={{
              flex: '1 1 0px',
              minHeight: 44,
            }}
            startIcon={<ConfirmIcon />}
            aria-label="Confirm deletion"
            loading={deleting}
          >
            Confirm
          </Button>
          <Button
            color="error"
            onClick={() => setDeleteID('')}
            variant="contained"
            sx={{
              flex: '1 1 0px',
              minHeight: 44,
            }}
            startIcon={<CancelIcon />}
            aria-label="Cancel deletion"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
